import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { useGetMonitoringSessionsQuery } from '../services/sessionMonitoringApi'
import { useCreateProjectUserSessionJourneyMutation } from '../services/projectDataApi'
import { Link } from './nav'
import ScreenshotViewer from './ScreenshotViewer'
import './DailyMonitoringSessions.css'

export default function DailyMonitoringSessions({ projectId, runId }: { projectId: string; runId: string }) {
  const [page, setPage] = useState(0)
  const query = useGetMonitoringSessionsQuery({ projectId, runId, page }, {
    pollingInterval: 10_000,
    skipPollingIfUnfocused: true,
  })
  const data = query.currentData
  const [createJourney] = useCreateProjectUserSessionJourneyMutation()
  const [workingSession, setWorkingSession] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [mergeSightmap, setMergeSightmap] = useState(false)

  async function requestJourney(sessionId: string, regenerate: boolean) {
    setWorkingSession(sessionId)
    setActionError(null)
    try {
      await createJourney({ projectId, sessionId, regenerate, mergeSightmap }).unwrap()
      await query.refetch()
    } catch {
      setActionError('Could not start journey creation. Please try again.')
    } finally {
      setWorkingSession(null)
    }
  }

  return <section className="daily-monitoring-sessions" aria-labelledby="daily-monitoring-sessions-title">
    <header>
      <h2 id="daily-monitoring-sessions-title" className="run-detail__section-title">User sessions{data && <> · {data.total.toLocaleString()}</>}</h2>
      <p>Sessions received during this monitoring day, including sessions not yet reviewed. Completed reviews with no observations are shown explicitly.</p>
    </header>
    {data?.can_edit && <label className="daily-monitoring-sessions__sightmap">
      <input type="checkbox" checked={mergeSightmap} onChange={event => setMergeSightmap(event.target.checked)} />
      Update the project Sightmap when creating a reproduction journey
    </label>}
    {query.isFetching && !data && <p role="status">Loading user sessions…</p>}
    {query.isError && <p role="alert">Could not load user sessions. <button type="button" onClick={() => void query.refetch()}>Retry</button></p>}
    {actionError && <p role="alert">{actionError}</p>}
    {data && data.sessions.length === 0 && <p>No user sessions were received during this monitoring day.</p>}
    {data?.sessions.map(session => <article key={session.session_id} className="daily-monitoring-sessions__session">
      <header>
        <div>
          <h3>{session.user_email || 'Anonymous user'}</h3>
          <time dateTime={session.first_received_at}>{new Date(session.first_received_at).toLocaleString(undefined, { timeZoneName: 'short' })}</time>
        </div>
        <div className="daily-monitoring-sessions__links">
          <Link to={`/projects/${projectId}/user-sessions/${encodeURIComponent(session.session_id)}`}>Session details</Link>
          <a href={session.external_session_url} target="_blank" rel="noreferrer">FullStory <ExternalLink size={13} aria-hidden /></a>
        </div>
      </header>
      {session.reviews.length === 0 && <p>Not reviewed.</p>}
      {session.reviews.map(review => <details className="daily-monitoring-sessions__review" key={review.id}>
        <summary>
          <strong>{review.reviewer}</strong>
          <span>{review.output ? `${review.output.observations.length} observations` : review.status}</span>
          {review.latest_id !== review.id && <span>Latest attempt: {review.latest_status}</span>}
        </summary>
        <div className="daily-monitoring-sessions__review-body">
          <Link to={`/projects/${projectId}/session-reviewers/${review.reviewer}`}>View reviewer</Link>
          {review.latest_id !== review.id && review.output && <p>Showing the previous saved result. Latest attempt: {review.latest_status}.</p>}
          {!review.output && <p>Review {review.status}. No saved result is available.</p>}
          {review.output?.observations.length === 0 && <p>Review completed with no observations.</p>}
          {review.output?.observations.map((observation, index) => <div className="daily-monitoring-sessions__observation" key={`${observation.code}:${index}`}>
            <h4>{observation.code}</h4>
            <p>{observation.description}</p>
            {Object.keys(observation.attributes).length > 0 && <dl>{Object.entries(observation.attributes).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}</dl>}
          </div>)}
          {review.output?.screenshot && <figure>
            <ScreenshotViewer frame={review.output.screenshot.image.url} alt={review.output.screenshot.caption} />
            <figcaption>{review.output.screenshot.caption} · {(review.output.screenshot.timestamp_ms / 1000).toFixed(3)}s into the session</figcaption>
          </figure>}
          {review.journey_id && <Link to={`/projects/${projectId}/journeys/${review.journey_id}?run=${encodeURIComponent(runId)}`}>Review reproduction journey</Link>}
        </div>
      </details>)}
      <div className="daily-monitoring-sessions__links">
        {session.journey_id && <Link to={`/projects/${projectId}/journeys/${session.journey_id}?run=${encodeURIComponent(runId)}`}>Reproduction journey</Link>}
        {data.can_edit && <button type="button" disabled={workingSession !== null || session.journey_status === 'inspecting_session' || session.journey_status === 'authoring_journey'} onClick={() => void requestJourney(session.session_id, Boolean(session.journey_id))}>
          {workingSession === session.session_id ? 'Starting journey…' : session.journey_status === 'inspecting_session' || session.journey_status === 'authoring_journey' ? 'Creating journey…' : session.journey_id ? 'Retry creating journey' : 'Create reproduction journey'}
        </button>}
        {session.journey_status && <span>Journey: {session.journey_status.replace(/_/g, ' ')}</span>}
      </div>
    </article>)}
    <nav className="daily-monitoring-sessions__pagination" aria-label="User session pages">
      <button type="button" disabled={page === 0 || query.isFetching} onClick={() => setPage(value => value - 1)}>Previous</button>
      <span>Page {page + 1}</span>
      <button type="button" disabled={!data?.has_more || query.isFetching} onClick={() => setPage(value => value + 1)}>Next</button>
    </nav>
  </section>
}
